import React, { createContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { MessageWithFeedback } from '@/shared/types/feedback';
import i18n from '@/shared/lib/i18n';

/**
 * StreamingContext - Global context for managing streaming state per session
 * 
 * This context persists streaming state when users navigate away from a session,
 * allowing them to return and see the ongoing processing or completed result.
 * Also provides data for the StreamingIndicator floating component.
 */

export interface StatusEntry {
    content: string;
    status: string;
}

export interface StreamingState {
    sessionId: string;
    projectId: string;
    projectName: string;
    sessionName: string;
    isProcessing: boolean;
    status: string | null;
    statusHistory: StatusEntry[];
    rawLog: string;
    thoughtLog: string;
    pendingUserMessage: MessageWithFeedback | null;
    userQuestion: string;
    startedAt: number;
}

export interface StreamingContextType {
    // Active sessions (reactive - causes re-render when state changes)
    activeSessions: StreamingState[];

    // Get all active streaming sessions (for indicator)
    getActiveSessions: () => StreamingState[];

    // Get streaming state for a specific session
    getStreamingState: (sessionId: string) => StreamingState | null;

    // Check if a session is currently processing
    isSessionProcessing: (sessionId: string) => boolean;

    // Check if any session is processing
    hasActiveStreaming: () => boolean;

    // Start streaming for a session (new query — has a pending message)
    startStreaming: (
        sessionId: string,
        projectId: string,
        projectName: string,
        sessionName: string,
        pendingMessage: MessageWithFeedback,
        question: string
    ) => void;

    // Reconnect to an in-progress stream after page reload (no pending message needed)
    reconnectStreaming: (
        sessionId: string,
        projectId: string,
        projectName: string,
        sessionName: string,
        question: string,
    ) => void;

    // Update streaming status
    updateStatus: (
        sessionId: string,
        status: string,
        rawLog?: string
    ) => void;

    // Add to status history
    addToStatusHistory: (sessionId: string, content: string, status: string) => void;

    // Append thought chunk
    appendThought: (sessionId: string, chunk: string) => void;

    // Complete streaming successfully
    completeStreaming: (sessionId: string) => void;

    // Fail streaming with error (keeps user message visible via getFailedUserMessage)
    failStreaming: (sessionId: string) => void;

    // Get user message that failed to send (so UI can keep it visible on network error)
    getFailedUserMessage: (sessionId: string) => MessageWithFeedback | null;

    // Clear streaming state for a session
    clearStreaming: (sessionId: string) => void;

    // Minimize state for indicator
    isMinimized: boolean;
    setIsMinimized: (value: boolean) => void;
}

const StreamingContext = createContext<StreamingContextType | undefined>(undefined);

// Cleanup stale sessions after 10 minutes
const STALE_SESSION_TIMEOUT = 10 * 60 * 1000;

export function StreamingProvider({ children }: { children: ReactNode }) {
    const [sessions, setSessions] = useState<Map<string, StreamingState>>(new Map());
    const [failedUserMessages, setFailedUserMessages] = useState<Map<string, MessageWithFeedback>>(new Map());
    const [isMinimized, setIsMinimized] = useState(false);

    // Cleanup stale sessions
    const cleanupStaleSessions = useCallback(() => {
        const now = Date.now();
        setSessions(prev => {
            const newMap = new Map(prev);
            let hasChanges = false;

            for (const [sessionId, state] of newMap.entries()) {
                if (now - state.startedAt > STALE_SESSION_TIMEOUT && !state.isProcessing) {
                    newMap.delete(sessionId);
                    hasChanges = true;
                }
            }

            return hasChanges ? newMap : prev;
        });
    }, []);

    // Get all active sessions for indicator
    const getActiveSessions = useCallback((): StreamingState[] => {
        return Array.from(sessions.values()).filter(s => s.isProcessing);
    }, [sessions]);

    // Get streaming state for a session
    const getStreamingState = useCallback((sessionId: string): StreamingState | null => {
        return sessions.get(sessionId) || null;
    }, [sessions]);

    // Check if session is processing
    const isSessionProcessing = useCallback((sessionId: string): boolean => {
        return sessions.get(sessionId)?.isProcessing || false;
    }, [sessions]);

    // Check if any session is processing
    const hasActiveStreaming = useCallback((): boolean => {
        return Array.from(sessions.values()).some(s => s.isProcessing);
    }, [sessions]);

    // Start streaming (clears any previous failed message for this session)
    const startStreaming = useCallback((
        sessionId: string,
        projectId: string,
        projectName: string,
        sessionName: string,
        pendingMessage: MessageWithFeedback,
        question: string
    ) => {
        setFailedUserMessages(prev => {
            const next = new Map(prev);
            next.delete(sessionId);
            return next;
        });
        setSessions(prev => {
            const newMap = new Map(prev);
            newMap.set(sessionId, {
                sessionId,
                projectId,
                projectName,
                sessionName,
                isProcessing: true,
                status: i18n.t('shared:processing.starting'),
                statusHistory: [] as StatusEntry[],
                rawLog: '',
                thoughtLog: '',
                pendingUserMessage: pendingMessage,
                userQuestion: question,
                startedAt: Date.now(),
            });
            return newMap;
        });
    }, []);

    // Reconnect to an in-progress stream (page reload — rebuild pendingMessage from Redis data)
    const reconnectStreaming = useCallback((
        sessionId: string,
        projectId: string,
        projectName: string,
        sessionName: string,
        question: string,
    ) => {
        setSessions(prev => {
            // Don't overwrite a session that's already being tracked (e.g. double-mount)
            if (prev.get(sessionId)?.isProcessing) return prev;
            const newMap = new Map(prev);
            newMap.set(sessionId, {
                sessionId,
                projectId,
                projectName,
                sessionName,
                isProcessing: true,
                status: i18n.t('shared:processing.reconnecting'),
                statusHistory: [] as StatusEntry[],
                rawLog: '',
                thoughtLog: '',
                pendingUserMessage: null,
                userQuestion: question,
                startedAt: Date.now(),
            });
            return newMap;
        });
    }, []);

    // Update status
    const updateStatus = useCallback((
        sessionId: string,
        status: string,
        rawLog?: string
    ) => {
        setSessions(prev => {
            const state = prev.get(sessionId);
            if (!state || !state.isProcessing) return prev;

            const newMap = new Map(prev);
            newMap.set(sessionId, {
                ...state,
                status,
                rawLog: rawLog ?? state.rawLog,
            });
            return newMap;
        });
    }, []);

    // Add to status history
    const addToStatusHistory = useCallback((sessionId: string, content: string, status: string) => {
        setSessions(prev => {
            const state = prev.get(sessionId);
            if (!state || !state.isProcessing) return prev;

            // Avoid consecutive duplicates
            if (state.statusHistory.length > 0 &&
                state.statusHistory[state.statusHistory.length - 1].content === content) {
                return prev;
            }

            const newMap = new Map(prev);
            newMap.set(sessionId, {
                ...state,
                statusHistory: [...state.statusHistory, { content, status }],
            });
            return newMap;
        });
    }, []);

    // Append thought log
    const appendThought = useCallback((sessionId: string, chunk: string) => {
        setSessions(prev => {
            const state = prev.get(sessionId);
            if (!state || !state.isProcessing) return prev;

            const newMap = new Map(prev);
            newMap.set(sessionId, {
                ...state,
                thoughtLog: state.thoughtLog + chunk,
            });
            return newMap;
        });
    }, []);

    // Complete streaming
    const completeStreaming = useCallback((sessionId: string) => {
        setSessions(prev => {
            const newMap = new Map(prev);
            // Just delete the session when complete
            newMap.delete(sessionId);
            return newMap;
        });
    }, []);

    // Fail streaming - keep user message so it stays visible (network error)
    const failStreaming = useCallback((sessionId: string) => {
        setSessions(prev => {
            const state = prev.get(sessionId);
            if (!state) return prev;
            if (state.pendingUserMessage) {
                setFailedUserMessages(failed => {
                    const next = new Map(failed);
                    next.set(sessionId, state.pendingUserMessage!);
                    return next;
                });
            }
            const newMap = new Map(prev);
            newMap.set(sessionId, {
                ...state,
                isProcessing: false,
                status: i18n.t('shared:processing.error'),
            });
            return newMap;
        });

        // Schedule cleanup
        setTimeout(cleanupStaleSessions, 5000);
    }, [cleanupStaleSessions]);

    const getFailedUserMessage = useCallback((sessionId: string): MessageWithFeedback | null => {
        return failedUserMessages.get(sessionId) ?? null;
    }, [failedUserMessages]);

    // Clear streaming state (and any failed message for this session)
    const clearStreaming = useCallback((sessionId: string) => {
        setFailedUserMessages(prev => {
            const next = new Map(prev);
            next.delete(sessionId);
            return next;
        });
        setSessions(prev => {
            const newMap = new Map(prev);
            newMap.delete(sessionId);
            return newMap;
        });
    }, []);

    // Derived value that causes re-renders when sessions change
    const activeSessions = useMemo(() => {
        return Array.from(sessions.values()).filter(s => s.isProcessing);
    }, [sessions]);

    const value: StreamingContextType = {
        activeSessions,
        getActiveSessions,
        getStreamingState,
        isSessionProcessing,
        hasActiveStreaming,
        startStreaming,
        reconnectStreaming,
        updateStatus,
        addToStatusHistory,
        appendThought,
        completeStreaming,
        failStreaming,
        getFailedUserMessage,
        clearStreaming,
        isMinimized,
        setIsMinimized,
    };

    return (
        <StreamingContext.Provider value={value}>
            {children}
        </StreamingContext.Provider>
    );
}
export { StreamingContext };
